import { ShadcnButton } from '@shadcn-features/core';
import { cx } from 'class-variance-authority';
import { Trash2Icon } from 'lucide-react';
import React, {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@src/components/ui/typography';
import { FlowTabProps } from './FlowTabView';

export const FlowTabVertical: FC<FlowTabProps> = ({
    items,
    changeItem,
    deleteTab,
    childrenBtnForFlowTab,
    tabProps,
}) => {
    const { t } = useTranslation();

    const [selected, setSelected] = useState<string | null>(items?.[0]?.key);
    const [isCheckData, setIsCheckData] = useState(true);

    const sectionRef = useRef<HTMLDivElement | null>(null);

    const setSelectedItem = useMemo(() => {
        return items.find((item) => item.key === selected);
    }, [items, selected]);

    const selectedItemIndex = useMemo(() => {
        return items.findIndex((item) => item.key === selected);
    }, [items, selected]);

    const handleScroll = useCallback(() => {
        sectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, []);

    const isActiveSteps = useCallback(
        (index: number) => {
            if (isCheckData) return true;
            return index <= selectedItemIndex;
        },
        [isCheckData, selectedItemIndex],
    );

    useEffect(() => {
        if (items.length > 0 && selected) {
            const isHaveSelectedKay = items.some(
                (item) => item.key === selected,
            );
            if (!isHaveSelectedKay) {
                setSelected(items?.[0]?.key);
            }
        } else if (items.length === 0 && selected) {
            setSelected(null);
        } else if (items.length > 0 && !selected) {
            setSelected(items?.[0]?.key);
        }
    }, [items, selected]);

    const handelChangeItem = useCallback(
        (selected: string) => {
            setIsCheckData(false);
            changeItem?.(selected);
            setSelected(selected);
            handleScroll();
        },
        [changeItem, handleScroll],
    );

    const handleSelectStep = useCallback(
        (key: string) => {
            setIsCheckData(false);
            setSelected(key);
            handleScroll();
        },
        [handleScroll],
    );

    return (
        <div
            ref={sectionRef}
            className={cx(
                'flex w-full flex-col items-end gap-3',
                tabProps?.className,
            )}
        >
            {items.length > 0 && (
                <button
                    className='font-inter cursor-pointer border-0 bg-transparent text-sm leading-5 font-medium text-[var(--color-brand-green)]'
                    type='button'
                    onClick={() => {
                        setIsCheckData((prev) => !prev);
                        handleScroll();
                    }}
                >
                    {isCheckData
                        ? t('translation:collapse')
                        : t('translation:showAll')}
                </button>
            )}

            <div className='flex w-full items-start gap-3.5'>
                <StepRail
                    isActiveSteps={isActiveSteps}
                    isCheckData={isCheckData}
                    items={items}
                    selected={selected}
                    onSelect={handleSelectStep}
                    onCheckData={() => {
                        setIsCheckData(true);
                        handleScroll();
                    }}
                />

                <div className='flex min-w-0 flex-1 flex-col'>
                    {!isCheckData && (
                        <StepCard>
                            <ShowCurrentStepChild
                                changeItem={handelChangeItem}
                                deleteTab={deleteTab}
                                items={items}
                                selected={selected}
                                selectedItemIndex={selectedItemIndex}
                                setSelectedItem={setSelectedItem}
                            />
                        </StepCard>
                    )}

                    {isCheckData && (
                        <ShowPageCheckData
                            changeItem={handelChangeItem}
                            deleteTab={deleteTab}
                            items={items}
                            selected={selected}
                            selectedItemIndex={selectedItemIndex}
                            setSelectedItem={setSelectedItem}
                        />
                    )}

                    {isCheckData && childrenBtnForFlowTab && (
                        <div className='mt-6 flex w-full flex-col gap-2 bg-[var(--color-bg-v4)]'>
                            {childrenBtnForFlowTab}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

function StepRail({
    items,
    selected,
    isCheckData,
    isActiveSteps,
    onSelect,
    onCheckData,
}: {
    items: FlowTabProps['items'];
    selected: string | null;
    isCheckData: boolean;
    isActiveSteps: (index: number) => boolean;
    onSelect: (key: string) => void;
    onCheckData: () => void;
}) {
    return (
        <div className='flex shrink-0 flex-col items-center gap-1.5 pt-2'>
            {items.map((item, index) => (
                <React.Fragment key={item.key}>
                    <StepButton
                        isActive={isActiveSteps(index)}
                        isCurrent={!isCheckData && selected === item.key}
                        label={index + 1}
                        onClick={() => onSelect(item.key)}
                    />
                    <StepLine />
                </React.Fragment>
            ))}
            <StepButton
                isActive={isCheckData}
                isCurrent={isCheckData}
                label={items.length + 1}
                onClick={onCheckData}
            />
        </div>
    );
}

function StepButton({
    label,
    isActive,
    isCurrent,
    onClick,
}: {
    label: number;
    isActive: boolean;
    isCurrent: boolean;
    onClick: () => void;
}) {
    return (
        <button
            aria-current={isCurrent ? 'step' : undefined}
            type='button'
            className={cx(
                'font-montserrat flex size-6 cursor-pointer items-center justify-center rounded-lg border-0 text-[15px] leading-5 font-semibold transition-colors',
                isActive
                    ? 'bg-[var(--color-brand-green)] text-[var(--color-card-text-revert)]'
                    : 'bg-[var(--color-bg-v6)] text-[var(--color-card-text)]',
            )}
            onClick={onClick}
        >
            {label}
        </button>
    );
}

function StepLine() {
    return <div className='h-[35px] w-px bg-[var(--color-bg-v5)]' />;
}

function StepCard({ children }: { children: React.ReactNode }) {
    return (
        <div className='w-full overflow-hidden rounded-xl bg-[var(--color-bg-v6)] p-5'>
            {children}
        </div>
    );
}

function ShowCurrentStepChild({
    selected,
    deleteTab,
    changeItem,
    setSelectedItem,
    selectedItemIndex,
    items,
    isReadonly,
}: {
    selected: string | null;
    deleteTab?: () => void;
    changeItem?: (id: string) => void;
    setSelectedItem?: FlowTabProps['items'][0];
    selectedItemIndex: number;
    items: FlowTabProps['items'];
    isReadonly?: boolean;
}) {
    const { t } = useTranslation();

    if (isReadonly) {
        return (
            <div className='flex flex-col'>
                <Typography.Title className='m-0 text-base font-bold' level={5}>
                    {setSelectedItem?.label}
                </Typography.Title>
                {setSelectedItem?.children && (
                    <div className='mt-5'>
                        {React.isValidElement(setSelectedItem.children)
                            ? React.cloneElement(setSelectedItem.children, {
                                  isReadOnly: true,
                                  classNameWrapper: 'gap-[0px]!',
                              })
                            : setSelectedItem.children}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className='w-full'>
            <div
                className='mb-[9px] flex items-center justify-between'
                hidden={isReadonly}
            >
                {selected && (
                    <span className='font-montserrat text-sm font-medium text-[var(--color-brand-green)]'>
                        {t('translation:step')} {selectedItemIndex + 1}
                    </span>
                )}
                {!selected && items.length === 0 && (
                    <ShadcnButton
                        className='text-[var(--color-danger)]!'
                        icon={<Trash2Icon size={14} />}
                        variant='icon'
                        popconfirm={{
                            title: t('translation:areYouSure'),
                            onConfirm: () => {
                                deleteTab?.();
                            },
                        }}
                    />
                )}
            </div>
            <span
                className='font-montserrat text-xl font-bold text-[var(--color-card-text)]'
                hidden={isReadonly}
            >
                {setSelectedItem?.label}
            </span>

            {setSelectedItem && (
                <div className='mt-[30px]'>{setSelectedItem.children}</div>
            )}
        </div>
    );
}

function ShowPageCheckData({
    ...props
}: Parameters<typeof ShowCurrentStepChild>[0]) {
    return (
        <div className='flex w-full flex-col gap-3'>
            {props?.items?.map((item) => (
                <div
                    key={item.key}
                    className='w-full rounded-xl bg-[var(--color-bg-v6)] p-5'
                >
                    <ShowCurrentStepChild
                        {...props}
                        isReadonly
                        changeItem={props.changeItem}
                        setSelectedItem={item}
                    />
                </div>
            ))}
        </div>
    );
}
